import { isAxiosError } from 'axios';
import { createLogger } from 'logging';

import {
    PaymentProfileResponse,
    UpdatePaymentProfileRequest,
    UploadTaxCertificateRequest,
} from '@videoblocks/subscription-service-client';

import { userApi } from './subscriptionServiceConfig';

const logger = createLogger('Payment Profile Api');

const getPaymentProfile = async (userId: number): Promise<PaymentProfileResponse | null> => {
    try {
        const response = await userApi.getPaymentProfile({ userId });
        return response.data ?? null;
    } catch (error) {
        if (isAxiosError(error)) {
            if (error.response?.status === 404) {
                logger.info({ userId }, 'Payment profile not found for user');
            } else if (error.response?.status === 400) {
                logger.warn({ error, userId }, 'Bad request for payment profile');
            } else {
                logger.error({ error, userId }, 'HTTP error fetching payment profile');
            }
        } else {
            logger.error({ error, userId }, 'Non-HTTP error fetching payment profile');
        }

        logger.error({ error, userId }, 'An error occurred while fetching payment profile');
        return null;
    }
};

const updatePaymentProfile = async (
    userId: number,
    updateRequest: UpdatePaymentProfileRequest,
): Promise<PaymentProfileResponse | null> => {
    try {
        const response = await userApi.updatePaymentProfile({
            userId,
            updatePaymentProfileRequest: updateRequest,
        });
        return response.data ?? null;
    } catch (error) {
        if (isAxiosError(error)) {
            if (error.response?.status === 400) {
                logger.warn({ error, userId }, 'Invalid payment profile update request');
            } else if (error.response?.status === 404) {
                logger.warn({ error, userId }, 'Payment profile not found');
            }
        }
        logger.error({ error, userId }, 'An error occurred while updating payment profile');
        return null;
    }
};

const uploadTaxCertificate = async (
    userId: number,
    uploadRequest: UploadTaxCertificateRequest,
): Promise<PaymentProfileResponse | null> => {
    try {
        const response = await userApi.uploadTaxCertificate({
            userId,
            uploadTaxCertificateRequest: uploadRequest,
        });
        return response.data ?? null;
    } catch (error) {
        if (isAxiosError(error)) {
            if (error.response?.status === 400) {
                logger.warn({ error, userId }, 'Invalid tax certificate upload request');
            } else if (error.response?.status === 404) {
                logger.warn({ error, userId }, 'Payment profile not found for tax certificate upload');
            }
        }
        logger.error({ error, userId }, 'An error occurred while uploading tax certificate');
        return null;
    }
};

export const PaymentProfileApi = {
    getPaymentProfile,
    updatePaymentProfile,
    uploadTaxCertificate,
};
